import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { api } from '../api';
import TiempoRelativo from './TiempoRelativo';
import { useCapa } from './navegacionReact';

// Centro de notificaciones: la campana del encabezado.
//
// Existía la tabla `notificaciones` desde la migración 004 y un módulo
// «Alertas» al que había que ENTRAR para verlas. Un aviso que hay que ir a
// buscar no avisa: el stock bajo de un producto se enteraba quien pasara
// por esa pantalla, que en un almacén ocupado es nadie.
//
// La campana está en el encabezado, visible desde cualquier módulo, y el
// contador no desaparece hasta que alguien lea. Es lo contrario del toast:
// el toast dice lo que acaba de pasar y se va; esto guarda lo que importa
// hasta que se atiende.
const CADA = 60_000;   // un sondeo por minuto: no es un chat

const TITULOS = {
  STOCK_BAJO: 'Stock bajo',
  SOLICITUD_PENDIENTE: 'Esperando autorización',
  ANOMALIA: 'Anomalía detectada',
  CADUCIDAD: 'Caducidad',
  ASIGNACION: 'Trabajo asignado',
};

export default function CentroNotificaciones({ onIr }) {
  const [avisos, setAvisos] = useState([]);
  const [abierto, setAbierto] = useState(false);
  useCapa(abierto, () => setAbierto(false));       // la lista de avisos: atrás la cierra
  const caja = useRef(null);

  const cargar = useCallback(
    () => api.get('/notificaciones?limite=50')
      .then(setAvisos)
      .catch(() => {}),   // sin conexión, la campana simplemente no cambia
    [],
  );

  useEffect(() => {
    let vivo = true;
    api.get('/notificaciones?limite=50')
      .then((d) => { if (vivo) setAvisos(d); })
      .catch(() => {});
    const iv = setInterval(() => { if (vivo) cargar(); }, CADA);
    return () => { vivo = false; clearInterval(iv); };
  }, [cargar]);

  // Cerrar al pulsar fuera: un panel que se queda abierto tapando la
  // pantalla es la razón por la que la gente deja de abrirlo.
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => {
      if (caja.current && !caja.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  const sinLeer = avisos.filter((a) => !a.leida);

  async function leer(id) {
    await api.post(`/notificaciones/${id}/leer`);
    setAvisos((as) => as.map((a) => (a.id === id ? { ...a, leida: true } : a)));
  }

  async function leerTodas() {
    await Promise.all(sinLeer.map((a) => api.post(`/notificaciones/${a.id}/leer`)));
    setAvisos((as) => as.map((a) => ({ ...a, leida: true })));
  }

  return (
    <div className="relative" ref={caja}>
      <button
        onClick={() => setAbierto((v) => !v)}
        aria-label={sinLeer.length ? `${sinLeer.length} avisos sin leer` : 'Avisos'}
        className="relative p-1.5 text-gray-500 hover:text-gray-800 rounded-lg hover:bg-gray-100"
      >
        <Bell className="h-5 w-5" />
        {sinLeer.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full
                           bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
            {sinLeer.length > 99 ? '99+' : sinLeer.length}
          </span>
        )}
      </button>

      {abierto && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border
                        border-gray-200 rounded-xl shadow-xl overflow-hidden z-30">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 gap-2">
            {/* Título=qué, subtítulo=para qué (Doctrina 2). */}
            <div className="min-w-0">
              <span className="block text-sm font-semibold text-gray-900">Avisos</span>
              <span className="block text-xs text-gray-500">Lo que TEGO te avisó y lo que espera algo de ti.</span>
            </div>
            {sinLeer.length > 0 && (
              <button onClick={leerTodas}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--color-marca)]">
                <CheckCheck className="h-3.5 w-3.5" /> Marcar todo leído
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
            {avisos.length === 0 && (
              <p className="px-3 py-6 text-sm text-gray-500 text-center">
                Nada pendiente. Aquí aparecerán los avisos de stock bajo, las
                autorizaciones que esperan, las anomalías y el trabajo que
                te asignen.
              </p>
            )}

            {avisos.map((a) => (
              <div key={a.id}
                   className={`px-3 py-2.5 flex gap-2 ${a.leida ? 'opacity-60' : 'bg-blue-50/40'}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {a.titulo || TITULOS[a.tipo] || a.tipo}
                  </div>
                  {a.mensaje && (
                    <div className="text-xs text-gray-600 leading-snug">{a.mensaje}</div>
                  )}
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    <TiempoRelativo valor={a.creado_en} />
                    {/* EL AVISO LLEVA A DONDE SE RESUELVE. Antes el único
                        camino de salida era el `sku`, que se inventó para
                        el aviso de stock bajo; cualquier otro aviso
                        terminaba en un texto que había que ir a buscar a
                        mano. `modulo` + `contexto` es el mismo contrato de
                        navegación que ya usan la búsqueda global y «Mi
                        trabajo». El `sku` se conserva: los avisos que ya
                        existían siguen saliendo igual. */}
                    {a.modulo && onIr && (
                      <button
                        onClick={() => { setAbierto(false); onIr(a.modulo, a.contexto || {}); }}
                        className="ml-2 font-semibold text-[var(--color-marca)]"
                      >
                        Ir a resolverlo
                      </button>
                    )}
                    {!a.modulo && a.sku && onIr && (
                      <button
                        onClick={() => { setAbierto(false); onIr('productos', { sku: a.sku }); }}
                        className="ml-2 font-semibold text-[var(--color-marca)]"
                      >
                        Ver {a.sku}
                      </button>
                    )}
                  </div>
                </div>
                {/* `title` solo sale al pasar el ratón: ni el teclado ni un
                    lector de pantalla lo ven. */}
                {!a.leida && (
                  <button type="button" onClick={() => leer(a.id)} title="Marcar leído"
                          aria-label={`Marcar como leído: ${a.titulo || a.mensaje || 'aviso'}`}
                          className="text-gray-500 hover:text-green-700 shrink-0 self-start rounded
                                     focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)]">
                    <Check className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
